// Device capability matcher: live media-query subscription for 3 axes
// (pointer modality, hover capability, reduced motion preference). The
// matcher is owned by bootstrap (main.ts/embed.ts) and injected into
// the orchestrator, which re-broadcasts via 'device-state' event.

export interface DeviceCapabilities {
  readonly pointerCoarse: boolean;
  readonly hoverNone: boolean;
  readonly reducedMotion: boolean;
}

// Step 7 Phase 0 — discrete device class for HD/SD adaptive delivery
// and mobile OOM guardrail. Snapshot one-shot: caller re-runs on
// orientation/foldable change if needed (rare). Foldable unfolded
// screens are conservatively classified as `mobile` (warning ON) —
// adopting `tablet` would skip the OOM guardrail on devices that
// still share WebKit memory budgets with phones.
export type DeviceClass = 'mobile' | 'desktop';

export interface DeviceClassInputs {
  readonly capabilities: DeviceCapabilities;
  readonly userAgent: string;
  readonly maxTouchPoints: number;
}

export function detectDeviceClass(inputs: DeviceClassInputs): DeviceClass {
  const { capabilities, userAgent, maxTouchPoints } = inputs;
  // iPadOS Safari in desktop mode lies in UA as Macintosh — only the
  // touch-points count distinguishes it. Treat as mobile for OOM
  // guardrail purposes (iPad's WebKit per-tab budget can still kill
  // 174 MB raw PLY tabs).
  const isIpadDesktopUa = /macintosh/i.test(userAgent) && maxTouchPoints > 1;
  if (capabilities.pointerCoarse || isIpadDesktopUa) return 'mobile';
  return 'desktop';
}

// Step 7 Phase 0 — capture environment inputs for detectDeviceClass.
// Wrapped behind a function so unit tests can inject a fake.
export function readDeviceClassInputs(
  capabilities: DeviceCapabilities,
  win: Window | undefined = typeof window === 'undefined' ? undefined : window,
): DeviceClassInputs {
  return {
    capabilities,
    userAgent: win?.navigator?.userAgent ?? '',
    maxTouchPoints: win?.navigator?.maxTouchPoints ?? 0,
  };
}

export interface DeviceMatcher {
  readonly snapshot: DeviceCapabilities;
  subscribe(cb: (snapshot: DeviceCapabilities) => void): () => void;
  dispose(): void;
}

const SAFE_DEFAULT: DeviceCapabilities = Object.freeze({
  pointerCoarse: false,
  hoverNone: false,
  reducedMotion: false,
});

// Legacy MediaQueryList API (Safari ≤14 / iOS ≤14): addListener /
// removeListener instead of addEventListener. Modern browsers expose
// both; we prefer addEventListener and fall back to addListener only
// when missing.
type LegacyMQL = MediaQueryList & {
  addListener?: (l: (e: MediaQueryListEvent) => void) => void;
  removeListener?: (l: (e: MediaQueryListEvent) => void) => void;
};

function attach(probe: MediaQueryList, listener: () => void): () => void {
  if (typeof probe.addEventListener === 'function') {
    probe.addEventListener('change', listener);
    return () => {
      try {
        probe.removeEventListener('change', listener);
      } catch {
        // ignore
      }
    };
  }
  // Legacy fallback. Wrap to ignore the event arg (we re-read all
  // probes on every change).
  const wrapped = (): void => listener();
  const legacy = probe as LegacyMQL;
  legacy.addListener?.(wrapped);
  return () => {
    try {
      legacy.removeListener?.(wrapped);
    } catch {
      // ignore
    }
  };
}

// SSR-safe default: use computed default param so callers in env
// without DOM (vitest happy-dom partial setup, Node tests) don't
// trigger a ReferenceError on `window`.
export function createDeviceMatcher(
  win: Window | undefined = typeof window === 'undefined' ? undefined : window,
): DeviceMatcher {
  if (win === undefined || typeof win.matchMedia !== 'function') {
    return {
      snapshot: SAFE_DEFAULT,
      subscribe: () => () => {},
      dispose: () => {},
    };
  }
  let pointerCoarseProbe: MediaQueryList | null = null;
  let hoverNoneProbe: MediaQueryList | null = null;
  let reducedMotionProbe: MediaQueryList | null = null;
  try {
    pointerCoarseProbe = win.matchMedia('(pointer: coarse)');
    hoverNoneProbe = win.matchMedia('(hover: none)');
    reducedMotionProbe = win.matchMedia('(prefers-reduced-motion: reduce)');
  } catch (err) {
    console.warn('[device] matchMedia threw:', err);
    return {
      snapshot: SAFE_DEFAULT,
      subscribe: () => () => {},
      dispose: () => {},
    };
  }

  const probes: readonly (MediaQueryList | null)[] = [
    pointerCoarseProbe,
    hoverNoneProbe,
    reducedMotionProbe,
  ];

  const readSnapshot = (): DeviceCapabilities => ({
    pointerCoarse: pointerCoarseProbe?.matches ?? false,
    hoverNone: hoverNoneProbe?.matches ?? false,
    reducedMotion: reducedMotionProbe?.matches ?? false,
  });

  let current: DeviceCapabilities = readSnapshot();

  const subs = new Set<(s: DeviceCapabilities) => void>();
  const refresh = (): void => {
    const next = readSnapshot();
    if (
      next.pointerCoarse === current.pointerCoarse &&
      next.hoverNone === current.hoverNone &&
      next.reducedMotion === current.reducedMotion
    ) {
      return; // reference equality on no-op
    }
    current = next;
    for (const cb of subs) cb(current);
  };

  const detachers: (() => void)[] = [];
  for (const probe of probes) {
    if (probe === null) continue;
    detachers.push(attach(probe, refresh));
  }

  return {
    get snapshot() {
      return current;
    },
    subscribe(cb) {
      subs.add(cb);
      return () => {
        subs.delete(cb);
      };
    },
    dispose() {
      for (const detach of detachers) detach();
      detachers.length = 0;
      subs.clear();
    },
  };
}
